import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    Box,
    Container,
    Typography,
    Breadcrumbs,
    Link,
    Paper,
    Chip,
    CircularProgress,
    Button,
    Alert,
    Snackbar,
    Card,
    CardContent,
    CardActionArea,
    Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Custom node components
const CompanyNode = ({ data }) => {
    return (
        <Card sx={{
            minWidth: 220,
            bgcolor: '#2196f3',
            color: 'white',
            border: '1px solid #1976d2',
            boxShadow: 3
        }}>
            <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h5" component="div" fontWeight="bold">
                    {data.label}
                </Typography>
                <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                    Coding Problems
                </Typography>
            </CardContent>
        </Card>
    );
};

const TopicNode = ({ data }) => {
    return (
        <Card sx={{
            minWidth: 180,
            bgcolor: '#4caf50',
            color: 'white',
            border: '1px solid #388e3c',
            boxShadow: 3
        }}>
            <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" component="div" fontWeight="medium">
                    {data.label}
                </Typography>
                <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                    Topic
                </Typography>
            </CardContent>
        </Card>
    );
};

const ProblemNode = ({ data }) => {
    const difficultyColor = {
        'EASY': '#4caf50',
        'easy': '#4caf50',
        'Easy': '#4caf50',
        'MEDIUM': '#ff9800',
        'medium': '#ff9800',
        'Medium': '#ff9800',
        'HARD': '#f44336',
        'hard': '#f44336',
        'Hard': '#f44336'
    }[data.difficulty] || '#9e9e9e';

    return (
        <Card sx={{
            minWidth: 220,
            boxShadow: 3,
            border: `1px solid ${difficultyColor}`
        }}>
            <CardActionArea
                component="a"
                href={data.link}
                target="_blank"
                rel="noopener noreferrer"
            >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="body1" component="div" fontWeight="medium" noWrap>
                        {data.label}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                        <Chip
                            label={data.difficulty}
                            size="small"
                            sx={{
                                bgcolor: difficultyColor,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {typeof data.acceptanceRate === 'number'
                                ? `${(data.acceptanceRate * 100).toFixed(1)}%`
                                : data.acceptanceRate}
                        </Typography>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

// Node types mapping
const nodeTypes = {
    companyNode: CompanyNode,
    topicNode: TopicNode,
    problemNode: ProblemNode
};

// Helper function to sort nodes by difficulty
const sortNodesByDifficulty = (nodes) => {
    // Define difficulty order: Easy -> Medium -> Hard
    const difficultyOrder = {
        'EASY': 1, 'easy': 1, 'Easy': 1,
        'MEDIUM': 2, 'medium': 2, 'Medium': 2,
        'HARD': 3, 'hard': 3, 'Hard': 3
    };

    // Group nodes by parent
    const nodesByParent = {};

    // First, find all parent-child relationships
    nodes.forEach(node => {
        if (node.parentNode) {
            if (!nodesByParent[node.parentNode]) {
                nodesByParent[node.parentNode] = [];
            }
            nodesByParent[node.parentNode].push(node);
        }
    });

    // Sort problem nodes by difficulty within each parent group
    Object.keys(nodesByParent).forEach(parentId => {
        nodesByParent[parentId].sort((a, b) => {
            if (a.type === 'problemNode' && b.type === 'problemNode') {
                return (difficultyOrder[a.data.difficulty] || 99) - (difficultyOrder[b.data.difficulty] || 99);
            }
            return 0;
        });

        // Update positions based on new order
        nodesByParent[parentId].forEach((node, index) => {
            node.position = { ...node.position, y: node.position.y + (index * 80) };
        });
    });

    return nodes;
};

const CompanyProblemTree = () => {
    const { companyId } = useParams();
    const navigate = useNavigate();

    const [company, setCompany] = useState(null);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);

    // Connect callback
    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, type: 'smoothstep', animated: true }, eds)),
        [setEdges]
    );

    // Fetch company details and problem tree data
    useEffect(() => {
        const fetchCompanyData = async () => {
            try {
                setLoading(true);

                // Step 1: Fetch company details
                const companyResponse = await fetch(`${process.env.REACT_APP_BACK_END_URL}/coding-problems/companies/${companyId}`);

                if (!companyResponse.ok) {
                    throw new Error(`Error fetching company: ${companyResponse.status}`);
                }

                const companyData = await companyResponse.json();
                setCompany(companyData);

                // Step 2: Fetch problem tree data
                const treeResponse = await fetch(`${process.env.REACT_APP_BACK_END_URL}/coding-problems/tree-data/${companyData.name}`);

                if (!treeResponse.ok) {
                    throw new Error(`Error fetching problem tree: ${treeResponse.status}`);
                }

                const treeData = await treeResponse.json();

                // Sort nodes by difficulty
                const sortedNodes = sortNodesByDifficulty(treeData.nodes);

                // Enhanced edges with better styling
                const enhancedEdges = treeData.edges.map(edge => ({
                    ...edge,
                    type: 'smoothstep',
                    animated: false,
                    style: { stroke: '#888', strokeWidth: 2 },
                    markerEnd: {
                        type: 'arrowclosed',
                        color: '#888',
                        width: 20,
                        height: 20
                    }
                }));

                // Set nodes and edges for ReactFlow
                setNodes(sortedNodes);
                setEdges(enhancedEdges);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching company data:', err);
                setError('Failed to load company data. Please try again later.');
                setShowAlert(true);
                setLoading(false);
            }
        };

        if (companyId) {
            fetchCompanyData();
        }
    }, [companyId, setNodes, setEdges]);

    // Handle alert close
    const handleAlertClose = () => {
        setShowAlert(false);
    };

    // Handle back button click
    const handleBackClick = () => {
        navigate('/companies');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Box my={4}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackClick}
                        sx={{ mb: 2 }}
                    >
                        Back to Companies
                    </Button>

                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                </Box>
            </Container>
        );
    }

    if (!company) {
        return null;
    }

    return (
        <Container maxWidth="lg">
            <Box my={4}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        sx={{ cursor: 'pointer' }}
                        onClick={handleBackClick}
                    >
                        Companies
                    </Link>
                    <Typography color="text.primary">{company.name}</Typography>
                </Breadcrumbs>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" component="h1">
                        {company.name} Coding Problems
                    </Typography>

                    <Chip
                        label={`${company.problem_count} Problems`}
                        color="primary"
                    />
                </Box>

                <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
                    <Typography variant="body1">
                        Explore coding problems asked by {company.name} organized by topic.
                        Click on any problem node to open the problem on LeetCode.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ height: 700, mb: 3 }}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        fitView
                        minZoom={0.5}
                        maxZoom={1.5}
                        defaultEdgeOptions={{
                            type: 'smoothstep',
                            style: { stroke: '#888', strokeWidth: 2 },
                            animated: true
                        }}
                    >
                        <Controls />
                        <MiniMap
                            nodeStrokeWidth={3}
                            zoomable
                            pannable
                            nodeBorderRadius={2}
                        />
                        <Background variant="dots" gap={12} size={1} />

                        <Panel position="top-right">
                            <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Difficulty Level
                                </Typography>
                                <Box display="flex" gap={1} alignItems="center">
                                    <Box display="flex" alignItems="center">
                                        <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: '#4caf50', mr: 0.5, borderRadius: '50%' }} />
                                        <Typography variant="caption">Easy</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: '#ff9800', mr: 0.5, borderRadius: '50%' }} />
                                        <Typography variant="caption">Medium</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: '#f44336', mr: 0.5, borderRadius: '50%' }} />
                                        <Typography variant="caption">Hard</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Panel>
                    </ReactFlow>
                </Paper>

                <Divider sx={{ my: 4 }} />

                <Box>
                    <Typography variant="h5" gutterBottom>
                        All {company.name} Problems
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(`/company/${companyId}/problems`)}
                        sx={{ mt: 1 }}
                    >
                        View All Problems
                    </Button>
                </Box>
            </Box>

            <Snackbar open={showAlert} autoHideDuration={6000} onClose={handleAlertClose}>
                <Alert onClose={handleAlertClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CompanyProblemTree;